import { useState } from "react";
import type { HospitalResultRow } from "./types";
import { fetchHospitalMisDocUrl } from "./api";

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function HospitalResultModal({
  row,
  onClose,
}: {
  row: HospitalResultRow;
  onClose: () => void;
}) {
  const [misBusy, setMisBusy] = useState(false);
  const [misError, setMisError] = useState<string | null>(null);

  const openInMis = async () => {
    if (!row.doc_id || !row.mis_id) {
      setMisError("У карты нет идентификатора документа или организации");
      return;
    }
    setMisBusy(true);
    setMisError(null);
    try {
      const url = await fetchHospitalMisDocUrl(row.doc_id, row.mis_id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setMisError(e instanceof Error ? e.message : String(e));
    } finally {
      setMisBusy(false);
    }
  };

  return (
    <div className="hospital-modal-overlay" onClick={onClose}>
      <div className="hospital-modal" onClick={(e) => e.stopPropagation()}>
        <div className="hospital-modal__header">
          <div>
            <div className="hospital-modal__title">Полный текст дефектов</div>
            <div className="hospital-modal__subtitle">{row.request_id}</div>
          </div>
          <div className="hospital-modal__actions">
            <button
              type="button"
              className="hospital-modal__mis"
              onClick={() => void openInMis()}
              disabled={misBusy || !row.doc_id || !row.mis_id}
              title="Открыть историю болезни в МИС"
            >
              {misBusy ? "Открываем…" : "Открыть в МИС"}
            </button>
            <button type="button" className="hospital-modal__close" onClick={onClose}>
              Закрыть
            </button>
          </div>
        </div>

        {misError ? <div className="hospital-modal__mis-error">Не удалось открыть в МИС: {misError}</div> : null}

        <div className="hospital-modal__grid">
          <div><strong>Дата выписки:</strong> {formatDate(row.discharge_date)}</div>
          <div><strong>Отделение:</strong> {row.department || "—"}</div>
          <div><strong>Врач:</strong> {row.doctor || "—"}</div>
          <div><strong>ИИН пациента:</strong> {row.patient_iin || "—"}</div>
          <div><strong>Диагноз:</strong> {row.diagnosis || "—"}</div>
          <div><strong>Тип стационара:</strong> {row.hospital_type || "—"}</div>
        </div>

        <div className="hospital-modal__section">
          <div className="hospital-modal__section-title">Ошибки</div>
          <div className="hospital-modal__defects">
            {row.defects.map((defect, idx) => (
              <div key={`${defect.rule_id}-${idx}`} className="hospital-modal__defect">
                <div className="hospital-modal__defect-title">
                  <span>{defect.defect_code}</span>
                  <span>{defect.title}</span>
                </div>
                <div className="hospital-modal__defect-line"><strong>Почему:</strong> {defect.reason}</div>
                <div className="hospital-modal__defect-line"><strong>Что ожидалось:</strong> {defect.expected}</div>
                <div className="hospital-modal__defect-line"><strong>Что пришло:</strong> {Object.entries(defect.actual).map(([k, v]) => `${k}=${String(v)}`).join(", ") || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        .hospital-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
          padding: 16px;
        }
        .hospital-modal {
          width: min(1100px, 96vw);
          max-height: 90vh;
          overflow: auto;
          background: #fff;
          border-radius: 16px;
          padding: 18px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.24);
          display: grid;
          gap: 16px;
        }
        .hospital-modal__header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
        }
        .hospital-modal__title {
          font-size: 20px;
          font-weight: 800;
          color: #0f172a;
        }
        .hospital-modal__subtitle {
          margin-top: 4px;
          color: #64748b;
          font-size: 13px;
        }
        .hospital-modal__close {
          border: 1px solid #e2e8f0;
          background: #fff;
          border-radius: 10px;
          padding: 8px 12px;
          cursor: pointer;
        }
        .hospital-modal__actions {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .hospital-modal__mis {
          border: 1px solid #0284c7;
          background: #0284c7;
          color: #fff;
          border-radius: 10px;
          padding: 8px 12px;
          cursor: pointer;
          font-weight: 600;
        }
        .hospital-modal__mis:disabled {
          opacity: 0.55;
          cursor: default;
        }
        .hospital-modal__mis-error {
          border: 1px solid #fecaca;
          background: #fef2f2;
          color: #b91c1c;
          border-radius: 10px;
          padding: 8px 12px;
          font-size: 13px;
        }
        .hospital-modal__grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px 16px;
          font-size: 14px;
        }
        .hospital-modal__section {
          display: grid;
          gap: 10px;
        }
        .hospital-modal__section-title {
          font-size: 16px;
          font-weight: 700;
        }
        .hospital-modal__defects {
          display: grid;
          gap: 12px;
        }
        .hospital-modal__defect {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 12px;
          background: #f8fafc;
          display: grid;
          gap: 8px;
        }
        .hospital-modal__defect-title {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
          font-weight: 700;
        }
        .hospital-modal__defect-title span:first-child {
          background: #e0f2fe;
          color: #075985;
          border-radius: 999px;
          padding: 2px 8px;
          font-size: 12px;
        }
        .hospital-modal__defect-line,
        .hospital-modal__evidence {
          font-size: 14px;
          line-height: 1.45;
          color: #0f172a;
        }
      `}</style>
    </div>
  );
}
